import { motion } from 'framer-motion';
import { BrainCircuit, ShieldCheck, CloudCog, Code2, Workflow, ArrowRight } from 'lucide-react';

const services = [
  {
  id: 'ai-data',
  icon: BrainCircuit,
  title: 'AI & Data',
  desc: 'Turn raw data into intelligent systems that automate decisions and uncover what you have been missing.',
  href: '/services/ai-data',
  img: 'https://cdn.flynaut.com/images/Data_and_AI.jpg',
  imgPosition: 'center 60%',
  span: 'md:col-span-2 lg:col-span-8 lg:row-span-2',
  isDark: true,
  },
  {
  id: 'cybersecurity',
  icon: ShieldCheck,
  title: 'Cybersecurity',
  desc: 'Proactive protection that stays ahead of threats. Risk management, compliance, and our OneProtect platform.',
  href: '/cybersecurity',
  img: 'https://cdn.flynaut.com/images/Cybersecurity_Flynaut.jpg',
  imgPosition: 'center center',
  span: 'lg:col-span-4 lg:row-span-2',
  isDark: true,
  },
  {
  id: 'cloud',
  icon: CloudCog,
  title: 'Cloud & Infrastructure',
  desc: 'Get to the cloud, optimize what you have, or go cloud-native. Strategy through execution.',
  href: '/services/cloud',
  img: 'https://cdn.flynaut.com/images/Cloud.jpg',
  imgPosition: 'center center',
  span: 'lg:col-span-4',
  isDark: true,
  },
  {
  id: 'app-dev',
  icon: Code2,
  title: 'Application Development',
  desc: 'Custom apps, legacy modernization, and enterprise platforms that actually perform.',
  href: '/services/application-development',
  img: 'https://cdn.flynaut.com/images/Application_Development.jpg',
  imgPosition: 'center center',
  span: 'lg:col-span-4',
  isDark: true,
  },
  {
  id: 'automation',
  icon: Workflow,
  title: 'Modernization & Automation',
  desc: 'Transform how your business operates. Automate what slows you down.',
  href: '/services/modernization-automation',
  img: 'https://cdn.flynaut.com/images/Modernization_and_Automation.jpg',
  imgPosition: 'center 40%',
  span: 'lg:col-span-4',
  isDark: true,
  },
];

function BentoCard({ service, delay }) {
  const Icon = service.icon;
  const isHero = service.span.includes('row-span-2');

  if (isHero) {
  return (
  <motion.a
  href={service.href}
  data-testid={`service-card-${service.id}`}
  initial={{ opacity: 0, y: 24 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true, margin: '-50px' }}
  transition={{ duration: 0.5, delay }}
  className={`group relative block ${service.span} rounded-2xl overflow-hidden min-h-[320px] lg:min-h-[440px] cursor-pointer border border-white/[0.06] hover:border-orange-500/40 transition-all duration-500`}
  style={{ background: '#0a0f1a' }}
  >
  {/* Background image - full bleed with hover zoom */}
  <div className="absolute inset-0 overflow-hidden">
  <img
  src={service.img}
  alt=""
  aria-hidden="true"
  className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-75 group-hover:scale-[1.06] transition-all duration-700 ease-out"
  style={{ objectPosition: service.imgPosition }}
  loading="lazy"
  />
  </div>

  {/* Multi-layer gradient overlay for text readability */}
  <div className="absolute inset-0 bg-gradient-to-t from-[#080d18]/95 via-[#0a0f1a]/50 to-transparent" />
  <div className="absolute inset-0 bg-gradient-to-r from-[#080d18]/40 to-transparent" />

  {/* Hover glow accent */}
  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-orange-500/[0.06] via-transparent to-transparent pointer-events-none" />

  {/* Content */}
  <div className="relative z-10 flex flex-col justify-end h-full p-8 lg:p-10">
  <div className="w-14 h-14 rounded-xl bg-white/[0.08] backdrop-blur-sm border border-white/[0.12] group-hover:bg-orange-500/15 group-hover:border-orange-500/30 flex items-center justify-center mb-5 group-hover:-translate-y-1 transition-all duration-400">
  <Icon size={28} className="text-orange-400 group-hover:text-orange-300 transition-colors duration-300" strokeWidth={1.5} />
  </div>
  <h3 className="font-display font-bold text-white text-2xl lg:text-3xl tracking-tight mb-3 group-hover:translate-x-1 transition-transform duration-400">{service.title}</h3>
  <p className="text-white/70 text-base lg:text-lg leading-relaxed max-w-md mb-6">{service.desc}</p>
  <span className="text-orange-400 text-sm font-semibold inline-flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
  Explore Service <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
  </span>
  </div>
  </motion.a>
  );
  }

  /* Compact dark image card for smaller grid cells */
  return (
  <motion.a
  href={service.href}
  data-testid={`service-card-${service.id}`}
  initial={{ opacity: 0, y: 24 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true, margin: '-50px' }}
  transition={{ duration: 0.5, delay }}
  className={`group relative block ${service.span} rounded-2xl overflow-hidden min-h-[260px] cursor-pointer border border-white/[0.06] hover:border-orange-500/40 transition-all duration-500`}
  style={{ background: '#0a0f1a' }}
  >
  {/* Background image */}
  <div className="absolute inset-0 overflow-hidden">
  <img
  src={service.img}
  alt=""
  aria-hidden="true"
  className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-[1.06] transition-all duration-700 ease-out"
  style={{ objectPosition: service.imgPosition }}
  loading="lazy"
  />
  </div>

  {/* Gradient overlay */}
  <div className="absolute inset-0 bg-gradient-to-t from-[#080d18]/95 via-[#0a0f1a]/60 to-[#0a0f1a]/20" />

  {/* Hover glow */}
  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-orange-500/[0.06] via-transparent to-transparent pointer-events-none" />

  {/* Content */}
  <div className="relative z-10 flex flex-col justify-end h-full p-7 lg:p-8">
  <div className="w-12 h-12 rounded-xl bg-white/[0.08] backdrop-blur-sm border border-white/[0.12] group-hover:bg-orange-500/15 group-hover:border-orange-500/30 flex items-center justify-center mb-4 group-hover:-translate-y-1 transition-all duration-400">
  <Icon size={22} className="text-orange-400 group-hover:text-orange-300 transition-colors duration-300" strokeWidth={1.5} />
  </div>
  <h3 className="font-display font-bold text-white text-xl tracking-tight mb-2 group-hover:translate-x-0.5 transition-transform duration-300">{service.title}</h3>
  <p className="text-white/60 text-sm leading-relaxed mb-4">{service.desc}</p>
  <span className="text-orange-400 text-sm font-semibold inline-flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
  Explore Service <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
  </span>
  </div>
  </motion.a>
  );
}

export default function ServicePillars() {
  return (
  <section
  id="services"
  data-testid="service-pillars-section"
  className="bg-navy-50 pt-16 lg:pt-20 pb-10 lg:pb-12"
  >
  <div className="max-w-content mx-auto px-6 xl:px-8">
  {/* Header */}
  <motion.div
  initial={{ opacity: 0, y: 24 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true, margin: '-80px' }}
  transition={{ duration: 0.5 }}
  className="mb-10 lg:mb-12"
  >
  <p className="text-overline text-orange-500 mb-3">CAPABILITIES</p>
  <h2 data-testid="service-pillars-heading" className="text-h2 text-navy-800 font-black tracking-tight mb-3">What We Do</h2>
  <p className="text-navy-500 text-base lg:text-lg max-w-xl">Five capabilities. One partner. Zero gaps between them.</p>
  </motion.div>

  {/* Bento Grid */}
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 auto-rows-auto mb-8">
  {services.map((service, i) => (
  <BentoCard key={service.id} service={service} delay={i * 0.08} />
  ))}
  </div>

  {/* CTA Banner */}
  <motion.div
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.5, delay: 0.2 }}
  data-testid="assessment-cta-banner"
  className="bg-navy-800 rounded-2xl px-8 py-7 flex flex-col sm:flex-row items-center justify-between gap-5"
  >
  <div>
  <p className="text-white font-display font-bold text-lg lg:text-xl">Not sure which service you need?</p>
  <p className="text-navy-400 text-sm mt-1">We will map your challenges to the right capabilities. No commitment.</p>
  </div>
  <a
  href="/assessment"
  data-testid="assessment-cta-button"
  className="group inline-flex items-center gap-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-full px-7 py-3.5 text-sm font-medium transition-all whitespace-nowrap flex-shrink-0"
  >
  Get a Free Technology Assessment <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
  </a>
  </motion.div>
  </div>
  </section>
  );
}
